import { QuickscreenOfferResponse } from '@weave/schema-gen-ts/dist/schemas/payments-platform/care-credit-integration/care_credit_integration.pb';
import { Person } from '@weave/schema-gen-ts/dist/schemas/persons/v3/persons.pb';
import { ContactType_Enum } from '@weave/schema-gen-ts/dist/shared/persons/v3/enums.pb';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useQuery, UseQueryOptions } from 'react-query';
import { usOptions, canadaOptions } from '@frontend/geography';
import { SchemaCareCreditIntegrationV1Service } from '../v1/service';

const allStatesMap = new Map([...usOptions, ...canadaOptions].map((state) => [state.label, state.value]));

dayjs.extend(utc);

type ContactType = 'MOBILE' | 'BUSINESS' | 'HOME';
type PhoneType = 'PHONE_MOBILE' | 'PHONE_WORK' | 'PHONE_HOME';
export const convert = {
  personPhoneToContactType: <T = ContactType_Enum>(type: T): ContactType => {
    switch (type) {
      case ContactType_Enum.PHONE_HOME:
        return 'HOME';
      case ContactType_Enum.PHONE_MOBILE:
        return 'MOBILE';
      case ContactType_Enum.PHONE_WORK:
        return 'BUSINESS';
      default:
        return 'HOME';
    }
  },
  contactTypeToPersonPhone: (type: ContactType | string): PhoneType => {
    switch (type) {
      case 'HOME':
        return ContactType_Enum.PHONE_HOME;
      case 'MOBILE':
        return ContactType_Enum.PHONE_MOBILE;
      case 'BUSINESS':
        return ContactType_Enum.PHONE_WORK;
      default:
        return ContactType_Enum.PHONE_HOME;
    }
  },
};

export const getPatientQueryFields = (person: Person | undefined) => {
  if (!person) {
    return null;
  }
  const email = person?.contactInfo?.find((contact) => contact.type === 'EMAIL')?.destination || '';
  const phone = person?.contactInfo?.find(
    (contact) => ['PHONE_MOBILE', 'PHONE_HOME', 'PHONE_WORK'].includes(contact.type) && contact.destination
  );
  const contactNumber = phone?.destination || '';
  const contactType = phone?.type ? convert.personPhoneToContactType(phone.type) : 'HOME';

  const dateOfBirth = person.birthdate ? dayjs(person.birthdate).utc().format('YYYYMMDD') : '';

  let state = person.address?.state || '';
  if (state.length > 2) {
    state = allStatesMap.get(state) || '';
  }

  const parsedData = {
    firstName: person.firstName || '',
    lastName: person.lastName || '',
    email,
    dateOfBirth,
    contactNumber,
    contactType,
    addressLine1: person.address?.street || '',
    city: person.address?.city || '',
    state,
    zipCode: person.address?.postalCode || '',
  };

  return parsedData;
};

const REQUIRED_FIELDS = ['firstName', 'lastName', 'addressLine1', 'city', 'state', 'zipCode'];

export const isPersonValidForQuery = (person: Person | undefined) => {
  const parsedFields = getPatientQueryFields(person);

  if (!parsedFields) {
    return false;
  }

  if (!REQUIRED_FIELDS.every((field) => !!parsedFields[field as keyof typeof parsedFields])) {
    return false;
  }

  // check first and last name for invalid characters
  const nameRegex = /[^a-zA-Z\s#,/.?@&^-]/;
  if (nameRegex.test(parsedFields.firstName) || nameRegex.test(parsedFields.lastName)) {
    return false;
  }

  return true;
};

export const getCareCreditPatientDataQueryKey = (person: Person | undefined) => {
  return [
    'careCreditPatientData',
    person?.personId ?? '',
    Object.values(getPatientQueryFields(person) || { unknown: 'unknown' }).join('-'),
  ];
};

type UseCareCreditPatientDataQueryProps = {
  merchantNumber: string | undefined;
  person: Person | undefined;
};
export const useCareCreditPatientDataQuery = (
  { merchantNumber, person }: UseCareCreditPatientDataQueryProps,
  options: UseQueryOptions<
    QuickscreenOfferResponse,
    unknown,
    QuickscreenOfferResponse,
    ReturnType<typeof getCareCreditPatientDataQueryKey>
  > = {}
) => {
  const { firstName, lastName, email, contactNumber, dateOfBirth, addressLine1, city, state, zipCode } =
    getPatientQueryFields(person) || {};

  const isPersonValid = isPersonValidForQuery(person);

  const query = useQuery({
    ...options,
    queryKey: getCareCreditPatientDataQueryKey(person),
    queryFn: async () => {
      return SchemaCareCreditIntegrationV1Service.QuickscreenOffer({
        checkAccountExistence: true,
        applicantInfo: {
          firstName,
          lastName,
          email,
          dateOfBirth,
          contactInfo: [
            {
              contactType: 'HOME', // CC's API docs lie. Only 'HOME' is accepted, even if we pass other contact types.
              contactNumber,
            },
          ],
          address: {
            addressLine1,
            city,
            state,
            zipCode,
          },
        },
        merchantNumber,
      });
    },
    enabled: isPersonValid && !!merchantNumber && options.enabled !== false,
  });

  return {
    query,
    isPersonValid,
  };
};
